import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import type { PickupInfo } from '@/types'
import CourierRatesTab, { type RateCardData } from './delivery/CourierRatesTab'
import SelfPickupTab from './delivery/SelfPickupTab'

interface DeliveryData {
  pickup: PickupInfo
  states: string[]
  cards: RateCardData[]
}

type Tab = 'rates' | 'pickup'

const pill = (active: boolean) =>
  cn(
    'rounded-lg border px-3.5 py-2 text-sm font-semibold',
    active ? 'border-primary/60 bg-primary/20 text-primary' : 'border-line text-muted hover:bg-foreground/5',
  )

/**
 * Delivery setup in two tabs: courier rate cards, and the self pickup card
 * and schedule customers see at checkout. Both tabs stay mounted and are
 * hidden rather than unmounted, so an unsaved draft survives switching.
 */
export default function AdminDelivery() {
  const queryClient = useQueryClient()
  // ?tab=pickup lets the booking modal's "Change" link land on the address card.
  const [tab, setTab] = useState<Tab>(() => (new URLSearchParams(window.location.search).get('tab') === 'pickup' ? 'pickup' : 'rates'))

  const { data, isLoading } = useQuery({
    queryKey: ['admin-delivery'],
    queryFn: async () => (await api.get<DeliveryData>('/admin/delivery')).data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-delivery'] })
    queryClient.invalidateQueries({ queryKey: ['shipping-info'] })
    queryClient.invalidateQueries({ queryKey: ['shipping-quote'] })
  }

  if (isLoading || !data) return <LogoLoader />

  return (
    <div>
      <PageHeader title="Delivery" subtitle="Courier rates by weight and zone, plus self pickup" />

      <div className="mb-4 flex flex-wrap gap-2">
        <button type="button" className={pill(tab === 'rates')} onClick={() => setTab('rates')}>
          Courier rates
        </button>
        <button type="button" className={pill(tab === 'pickup')} onClick={() => setTab('pickup')}>
          Self pickup
        </button>
      </div>

      <div className={cn(tab !== 'rates' && 'hidden')}>
        <CourierRatesTab initial={data.cards} onSaved={invalidate} />
      </div>
      <div className={cn(tab !== 'pickup' && 'hidden')}>
        <SelfPickupTab initial={data.pickup} states={data.states} onSaved={invalidate} />
      </div>
    </div>
  )
}
